import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Button, VStack } from 'wss3-forge'
import { ArrowDown16Regular } from '@fluentui/react-icons'
import { ChatMessage } from './ChatMessage.js'
import { ActivityStrip } from './ActivityStrip.js'
import type { StoredMessage } from '../lib/chat-storage.js'
import type { ChatActivity } from '../hooks/useChat.js'

const NEAR_BOTTOM_THRESHOLD_PX = 80

export interface MessageListProps {
  messages: StoredMessage[]
  isThinking: boolean
  /** Live agent steps shown while a turn is in flight. */
  activities?: ChatActivity[]
}

/**
 * Smart auto-scroll: stick to the bottom while the user is already there
 * (typical chat flow), but stop following if they scroll up to read history.
 * A small "new message" pill appears as an explicit affordance to jump back.
 */
export function MessageList({ messages, isThinking, activities = [] }: MessageListProps) {
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const bottomRef = useRef<HTMLDivElement | null>(null)
  const [stickToBottom, setStickToBottom] = useState(true)
  const [hasUnreadBelow, setHasUnreadBelow] = useState(false)

  const isNearBottom = useCallback(() => {
    const el = scrollerRef.current
    if (!el) return true
    return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_THRESHOLD_PX
  }, [])

  // Track whether the user is near the bottom; toggle stickiness accordingly.
  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    const onScroll = () => {
      const near = isNearBottom()
      setStickToBottom(near)
      if (near) setHasUnreadBelow(false)
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [isNearBottom])

  // After messages change, either stick to bottom or surface the pill.
  // Use 'auto' (instant) while thinking so the indicator doesn't queue smooth-scroll
  // animations on slow devices; 'smooth' once a real message lands.
  useLayoutEffect(() => {
    if (stickToBottom) {
      const behavior: ScrollBehavior = isThinking ? 'auto' : 'smooth'
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ behavior, block: 'end' })
      })
      setHasUnreadBelow(false)
    } else if (messages.length > 0) {
      setHasUnreadBelow(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, isThinking])

  const jumpToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    setStickToBottom(true)
    setHasUnreadBelow(false)
  }

  // ChatPage owns the welcome state (messages.length === 0). When MessageList
  // mounts we always have at least one message or an in-flight turn.
  return (
    <div
      ref={scrollerRef}
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        position: 'relative',
        // Inner scroll container so smart auto-scroll observes the right element.
        // (ChatPage also wraps us — its own overflow:auto becomes a no-op since
        // our content never exceeds; we own the scroll.)
      }}
    >
      <VStack
        gap="xl"
        align="stretch"
        fullWidth
        style={{
          padding: 'var(--spacing-lg) var(--spacing-lg) var(--spacing-xl)',
          maxWidth: 880,
          marginInline: 'auto',
          width: '100%',
        }}
      >
        {messages.map((m, i) => (
          <ChatMessage
            key={i}
            role={m.role}
            content={m.content}
            {...(m.toolCalls ? { toolCalls: m.toolCalls } : {})}
            {...(m.attachments ? { attachments: m.attachments } : {})}
          />
        ))}
        {isThinking && <ActivityStrip activities={activities} />}
        <div ref={bottomRef} />
      </VStack>

      {hasUnreadBelow && (
        <div
          style={{
            position: 'sticky',
            bottom: 12,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <div style={{ pointerEvents: 'auto' }}>
            <Button
              variant="secondary"
              size="sm"
              icon={<ArrowDown16Regular />}
              onClick={jumpToBottom}
            >
              Nouveau message
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
